import { FLOATING_ELEMENT_PLACEMENT } from '../../../../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../../../../constants/date'
import Button, { BUTTON_HEIGHT_32 } from '../../../../../Button'
import {
  ICON_COLOR,
  ICON_HEIGHT_16,
  IconWithTooltip,
} from '../../../../../Icon'
import {
  APPLICANT_MASKED_TEXT_COLOR,
  APPLICANT_STAGE,
  APPLICANT_STAGES,
  getApplicantCreditCheckBandProps,
  getIsApplicantFieldMasked,
  RentalPortalApplicantIdentityCell,
} from '../../../../../RentalPortalApplicant'
import { Status, STATUS_SIZE_20 } from '../../../../../Status'
import {
  TableCellBase,
  TableDateCell,
  TableEmptyCell,
} from '../../../../../Table'
import Text from '../../../../../Text'
import {
  APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR,
  getIsApplicantCreditCheckInconclusive,
  getIsApplicantCreditCheckResultExpired,
} from '../../../../RentalPortalApplicantProfile'
import {
  APPLICANT_COMPARISON_INCOME_STATUS_LABEL_KEY,
  APPLICANT_COMPARISON_INCOME_STATUS_TEXT_COLOR,
  APPLICANT_COMPARISON_LABEL_COLUMN,
  APPLICANT_COMPARISON_ROW,
  QA_NAME_APPLICANT_COMPARISON_APPROVE,
  QA_NAME_APPLICANT_COMPARISON_BAND,
  QA_NAME_APPLICANT_COMPARISON_DOCUMENTS,
  QA_NAME_APPLICANT_COMPARISON_EXPIRED,
  QA_NAME_APPLICANT_COMPARISON_PROFILE,
  QA_NAME_APPLICANT_COMPARISON_PROTECTED,
  QA_NAME_APPLICANT_COMPARISON_PROVIDER,
  QA_NAME_APPLICANT_COMPARISON_SELECT,
  QA_NAME_APPLICANT_COMPARISON_SELECTED,
} from '../RentalPortalApplicantComparison.constants'
import {
  getIsApplicantComparisonSelectable,
  getIsApplicantComparisonSelected,
} from '../RentalPortalApplicantComparison.utils'

import CheckmarkIcon from '../../../../../../icons/checkmark.svg'

function renderApplicantComparisonDocumentsCell({
  applicant,
  cellProps,
  classes,
  t,
}) {
  const documents = applicant?.documents

  const progressText = t('RentalPortal.ApplicantCheckDocumentsProgressKey', {
    required: documents?.required,
    verified: documents?.verified,
  })

  return documents?.required == null ? (
    <TableEmptyCell {...cellProps} />
  ) : (
    <TableCellBase {...cellProps}>
      {documents.complete ? (
        <div className={classes.documentsComplete}>
          <IconWithTooltip
            Icon={CheckmarkIcon}
            color={ICON_COLOR.CurrentColor}
            qaName={QA_NAME_APPLICANT_COMPARISON_DOCUMENTS}
            size={ICON_HEIGHT_16}
            tooltip={progressText}
          />
        </div>
      ) : (
        <Text className={classes.cellValue}>{progressText}</Text>
      )}
    </TableCellBase>
  )
}

function renderApplicantComparisonIncomeCell({
  applicant,
  cellProps,
  classes,
  t,
}) {
  const labelKey =
    APPLICANT_COMPARISON_INCOME_STATUS_LABEL_KEY[applicant?.income_proof_status]

  return labelKey ? (
    <TableCellBase {...cellProps}>
      <Status
        className={classes.chip}
        height={STATUS_SIZE_20}
        text={t(labelKey)}
        textColor={
          APPLICANT_COMPARISON_INCOME_STATUS_TEXT_COLOR[
            applicant?.income_proof_status
          ]
        }
      />
    </TableCellBase>
  ) : (
    <TableEmptyCell {...cellProps} />
  )
}

function renderApplicantComparisonCreditCheckCell({
  applicant,
  cellProps,
  classes,
  t,
}) {
  const result = applicant?.credit_check?.expected

  if (!result) {
    return (
      <TableCellBase {...cellProps}>
        <Text isSecondary className={classes.cellValue}>
          {t('RentalPortal.ApplicantCreditCheckNoVerdictKey')}
        </Text>
      </TableCellBase>
    )
  }

  const isMasked = getIsApplicantFieldMasked(result.normalised_band)

  const bandProps = getApplicantCreditCheckBandProps({
    field: result.normalised_band,
    t,
  })

  return (
    <TableCellBase {...cellProps}>
      <div className={classes.creditCheck}>
        <Text
          className={classes.provider}
          qaName={QA_NAME_APPLICANT_COMPARISON_PROVIDER}
        >
          {result.provider_display_name}
        </Text>
        {isMasked || result.normalised_band?.value ? (
          <Status
            {...bandProps}
            className={classes.chip}
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_COMPARISON_BAND}
            tooltip={t('RentalPortal.ApplicantComparisonCreditCheckCellKey', {
              band: bandProps.text,
              provider: result.provider_display_name,
            })}
          />
        ) : (
          <Text isSecondary className={classes.cellValue}>
            {t('RentalPortal.ApplicantCreditCheckNoVerdictKey')}
          </Text>
        )}
        {getIsApplicantCreditCheckResultExpired(result) ? (
          <Status
            className={classes.chip}
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_COMPARISON_EXPIRED}
            text={t('RentalPortal.ApplicantCreditCheckExpiredChipKey')}
            textColor={APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR}
          />
        ) : null}
        {getIsApplicantCreditCheckInconclusive(result) ? (
          <Text isSecondary className={classes.cellNote}>
            {t('RentalPortal.ApplicantCreditCheckNoDataHeldKey', {
              provider: result.provider_display_name,
            })}
          </Text>
        ) : null}
      </div>
    </TableCellBase>
  )
}

function renderApplicantComparisonProtectedCell({
  applicant,
  cellProps,
  classes,
  t,
}) {
  return (
    <TableCellBase {...cellProps}>
      <div className={classes.creditCheck}>
        {getIsApplicantFieldMasked(applicant?.protected_characteristics) ? (
          <Status
            className={classes.chip}
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_COMPARISON_PROTECTED}
            text={t('RentalPortal.ApplicantProfileMaskedValueKey')}
            textColor={APPLICANT_MASKED_TEXT_COLOR}
          />
        ) : null}
        <Text isSecondary className={classes.cellNote}>
          {t('RentalPortal.ApplicantComparisonAggMaskedKey')}
        </Text>
      </div>
    </TableCellBase>
  )
}

function renderApplicantComparisonActionsCell({
  applicant,
  cellProps,
  classes,
  hasSelection,
  t,
  onClickProfile,
  onClickSelect,
}) {
  const selectedStage = APPLICANT_STAGES[APPLICANT_STAGE.selected]

  function handleClickProfile() {
    onClickProfile(applicant?.id)
  }

  function handleClickSelect() {
    onClickSelect(applicant)
  }

  return (
    <TableCellBase {...cellProps}>
      <div className={classes.actions}>
        <Button.Outlined
          height={BUTTON_HEIGHT_32}
          qaName={QA_NAME_APPLICANT_COMPARISON_PROFILE}
          text={t('RentalPortal.ApplicantComparisonProfileBtnKey')}
          onClick={handleClickProfile}
        />
        {getIsApplicantComparisonSelected(applicant) ? (
          <Status
            className={classes.chip}
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_COMPARISON_SELECTED}
            text={t(selectedStage.text)}
            textColor={selectedStage.textColor}
          />
        ) : null}
        {getIsApplicantComparisonSelectable(applicant) ? (
          <Button.Contained
            height={BUTTON_HEIGHT_32}
            qaName={QA_NAME_APPLICANT_COMPARISON_SELECT}
            text={t(
              hasSelection
                ? 'RentalPortal.ApplicantComparisonChangeSelectionBtnKey'
                : 'RentalPortal.ApplicantComparisonSelectBtnKey'
            )}
            onClick={handleClickSelect}
          />
        ) : null}
      </div>
    </TableCellBase>
  )
}

function renderApplicantComparisonFourEyesCell({ cellProps, t }) {
  return (
    <TableCellBase {...cellProps}>
      <Button.Outlined
        disabled
        height={BUTTON_HEIGHT_32}
        qaName={QA_NAME_APPLICANT_COMPARISON_APPROVE}
        text={t('RentalPortal.ApplicantDecisionApproveBtnKey')}
        tooltip={t('RentalPortal.ApplicantComparisonApproveDisabledTooltipKey')}
        tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
      />
    </TableCellBase>
  )
}

export function renderApplicantComparisonCell({
  applicant,
  classes,
  hasSelection,
  name,
  t,
  onClickProfile,
  onClickSelect,
  ...cellProps
}) {
  switch (name) {
    case APPLICANT_COMPARISON_ROW.applicant:
      return (
        <RentalPortalApplicantIdentityCell
          {...cellProps}
          applicant={applicant}
        />
      )

    case APPLICANT_COMPARISON_ROW.documents:
      return renderApplicantComparisonDocumentsCell({
        applicant,
        cellProps,
        classes,
        t,
      })

    case APPLICANT_COMPARISON_ROW.income:
      return renderApplicantComparisonIncomeCell({
        applicant,
        cellProps,
        classes,
        t,
      })

    case APPLICANT_COMPARISON_ROW.creditCheck:
      return renderApplicantComparisonCreditCheckCell({
        applicant,
        cellProps,
        classes,
        t,
      })

    case APPLICANT_COMPARISON_ROW.protected:
      return renderApplicantComparisonProtectedCell({
        applicant,
        cellProps,
        classes,
        t,
      })

    case APPLICANT_COMPARISON_ROW.received:
      return (
        <TableDateCell
          {...cellProps}
          dateFormat={DATE_SHORT_FORMAT}
          useTodayFormat={false}
          value={applicant?.received}
        />
      )

    case APPLICANT_COMPARISON_ROW.actions:
      return renderApplicantComparisonActionsCell({
        applicant,
        cellProps,
        classes,
        hasSelection,
        t,
        onClickProfile,
        onClickSelect,
      })

    case APPLICANT_COMPARISON_ROW.fourEyes:
      return renderApplicantComparisonFourEyesCell({ cellProps, t })

    default:
      return <TableEmptyCell {...cellProps} />
  }
}

export function getIsApplicantComparisonLabelColumn(column) {
  return column.name === APPLICANT_COMPARISON_LABEL_COLUMN
}
